/* component-free-gift-bar.js — <free-gift-bar>
   BXGY free-gift progress bar. Works inside the cart drawer AND on the cart page.

   Server (snippets/free-gift-bar.liquid) computes the display state and stamps
   data attributes:
     data-threshold        gift threshold in cents
     data-gift-variant-id  fallback gift variant id (first available single size)
     data-gift-in-cart     'true' if the gift line is present
     data-auto-remove      'true' to auto-remove the gift when cart drops below

   BF 2026 (LT): the customer picks the gift size in a <select data-gift-size>;
   the selected variant is added with a { _gift: 'true' } line property. The gift
   line is identified ONLY by that property, so a normally purchased Cloud pillow
   is never mistaken for (or removed as) the gift.

   Talks to the cart ONLY through window.KK.cart. ES module, one component/file. */

class FreeGiftBar extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.getAttribute('data-threshold') || '0', 10);
    this.giftVariantId = this.getAttribute('data-gift-variant-id') || '';
    this.autoRemove = this.getAttribute('data-auto-remove') === 'true';
    this._adding = false;
    this._removing = false;

    const btn = this.querySelector('[data-gift-add]');
    if (btn) {
      this._onAdd = (e) => { e.preventDefault(); this.addGift(); };
      btn.addEventListener('click', this._onAdd);
      this._addBtn = btn;
    }

    if (window.KK && window.KK.pubsub) {
      this._unsub = window.KK.pubsub.subscribe('kk:cart:updated', (p) => this._onCartUpdated(p));
    }
  }

  disconnectedCallback() {
    if (this._addBtn && this._onAdd) this._addBtn.removeEventListener('click', this._onAdd);
    if (this._unsub) this._unsub();
  }

  _selectedVariantId() {
    const sel = this.querySelector('[data-gift-size]');
    if (sel && sel.value) return sel.value;
    return this.giftVariantId;
  }

  addGift() {
    const variantId = this._selectedVariantId();
    if (this._adding || !variantId) return;
    if (!(window.KK && window.KK.cart)) return;
    this._adding = true;
    if (this._addBtn) this._addBtn.setAttribute('disabled', '');
    const opts = {};
    const drawer = document.querySelector('cart-drawer[data-section-id]');
    if (drawer) opts.sections = [drawer.getAttribute('data-section-id')];
    window.KK.cart
      .add({ id: Number(variantId), quantity: 1, properties: { _gift: 'true' } }, opts)
      .catch(() => {})
      .finally(() => { this._adding = false; if (this._addBtn) this._addBtn.removeAttribute('disabled'); });
  }

  _onCartUpdated(payload) {
    const cart = (payload && payload.cart) || (window.KK && window.KK.cart && window.KK.cart.state);
    if (!cart) return;

    const giftLine = (cart.items || []).find((it) => {
      const props = it.properties || {};
      return !!props._gift;
    });

    const belowThreshold = this.threshold > 0 && cart.total_price < this.threshold;

    if (this.autoRemove && belowThreshold && giftLine && !this._removing) {
      this._removing = true;
      if (window.KK && window.KK.cart) {
        window.KK.cart.change(giftLine.key, 0)
          .catch(() => {})
          .finally(() => { this._removing = false; });
      }
      return;
    }

    if (giftLine) {
      this._neutraliseAddButton();
    }
  }

  _neutraliseAddButton() {
    this.setAttribute('data-gift-in-cart', 'true');
    const pick = this.querySelector('[data-gift-pick]');
    if (pick) pick.hidden = true;
    const btn = this._addBtn || this.querySelector('[data-gift-add]');
    if (!btn) return;
    btn.setAttribute('disabled', '');
    btn.setAttribute('aria-hidden', 'true');
    btn.hidden = true;
    if (this._onAdd) {
      btn.removeEventListener('click', this._onAdd);
      this._onAdd = null;
    }
  }
}

if (!customElements.get('free-gift-bar')) {
  customElements.define('free-gift-bar', FreeGiftBar);
}
